import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { ButtonMedium } from '@components/core-ui/form-elements/buttons/Button/variations/PrimaryButtonSizes';
import { PasswordField } from '@components/core-ui/form-elements/PasswordField';
import CheckEmail from '@components/icons/CheckEmail.svg';
import { Spinner } from '@components/core-ui/spinners/Spinner';
import { PASSWORD_REQUIREMENTS_HINT } from '@helpers/validatePassword';
import { usePasswordFieldErrors } from 'app/hooks/usePasswordFieldErrors';
import { routes } from '@routes';
import PositiveIcon from 'app/components/icons/Positive.svg';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import { usePageContext } from '../../providers/PageProvider';
import { StyledIconWrapper } from './styles/StyledIconWrapper';
import { Wrapper } from './styles/Wrapper';

export const PasswordResetPageContent = () => {
  const {
    password,
    setPassword,
    confirmation,
    setConfirmation,
    error,
    setError,
    isLoading,
    reset,
    isComplete,
    isExpired,
  } = usePageContext();
  const navigate = useNavigate();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const {
    passwordRef,
    confirmationRef,
    passwordError,
    confirmationError,
    onPasswordBlur,
    onConfirmationBlur,
    validatePasswordFields,
  } = usePasswordFieldErrors(password || '', confirmation || '');

  const goToLogin: React.MouseEventHandler<HTMLButtonElement> = (evt) => {
    evt.preventDefault();
    navigate(routes.signin.make());
  };

  const goToRecovery: React.MouseEventHandler<HTMLButtonElement> = (evt) => {
    evt.preventDefault();
    navigate(routes.passwordRecovery.make());
  };

  const handleFormSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    if (validatePasswordFields()) {
      reset();
    }
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const goToRegistration = (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();
    navigate(routes.signup.make());
  };

  if (isComplete) {
    return (
      <Wrapper>
        <div className="root">
          <StyledIconWrapper>
            <PositiveIcon className="positiveIcon" />
          </StyledIconWrapper>
          <h1 className="completePasswordReset">Password Reset</h1>
          <h2 className="completeYourPasswordHasBeen">
            Your password has been successfully reset. <br />
            Click below to log in.
          </h2>
          <ButtonMedium className="mb-[30px] mx-auto w-full" onClick={goToLogin}>
            Continue
          </ButtonMedium>
        </div>
      </Wrapper>
    );
  }

  if (isExpired) {
    return (
      <Wrapper>
        <div className="root">
          <StyledIconWrapper>
            <CheckEmail className="checkEmailIcon" />
          </StyledIconWrapper>
          <h1 className="completePasswordReset">Password Reset</h1>
          <h2 className="completeYourPasswordHasBeen">
            The password reset link is expired. <br />
            Please start the recovery process again.
          </h2>
          <ButtonMedium className="mb-[30px] mx-auto w-full" onClick={goToRecovery}>
            Return
          </ButtonMedium>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {isLoading && <Spinner overlay={true} />}
      <div className="root">
        <div className="content">
          <div>
            <h1 className="setNewPassword">Set New Password</h1>
            <h2 className="yourNewPassword">Your new password must be different to previously used passwords.</h2>
          </div>

          <div className="form">
            <form onSubmit={handleFormSubmit}>
              <div className="mb-[30px]">
                <PasswordField
                  ref={passwordRef}
                  name="password"
                  label="Password"
                  value={password || ''}
                  onChange={(value) => {
                    setError('');
                    setPassword(value);
                  }}
                  onBlur={onPasswordBlur}
                  isVisible={isPasswordVisible}
                  onVisibilityToggle={togglePasswordVisibility}
                  hint={PASSWORD_REQUIREMENTS_HINT}
                  error={passwordError}
                  autoComplete="new-password"
                  required={true}
                />
              </div>
              <div className="mb-[30px]">
                <PasswordField
                  ref={confirmationRef}
                  name="confirmation"
                  label="Confirm password"
                  value={confirmation ?? ''}
                  onChange={(value) => {
                    setError('');
                    setConfirmation(value);
                  }}
                  onBlur={onConfirmationBlur}
                  isVisible={isPasswordVisible}
                  onVisibilityToggle={togglePasswordVisibility}
                  error={confirmationError}
                  autoComplete="new-password"
                  required={true}
                />
              </div>

              {error ? (
                <div className="formError">
                  <ErrorMessage error={error} />
                </div>
              ) : null}

              <ButtonMedium
                disabled={!password || !confirmation || password !== confirmation || isLoading}
                type="submit"
                className="mb-[30px]"
              >
                Reset Password
              </ButtonMedium>

              <p className="dontHaveAnAccount">
                {"Don't have an account yet? "}&nbsp;&nbsp;
                <span onClick={goToRegistration} className="signUp">
                  Sign Up
                </span>
              </p>
            </form>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};
